import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FicheConseil, Investisseur } from '@lib/models/generated/graphql';
import { PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE } from '@lib/models/graphqlData';
import { OuiDialogService } from 'omnium-ui/dialog';
import { Subject, takeUntil } from 'rxjs';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import { SearchInvestisseurAutocompleteComponent } from 'src/app/components/search-investisseur-autocomplete/search-investisseur-autocomplete.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';

@Component({
  selector: 'app-investisseur-step',
  templateUrl: './investisseur-step.component.html',
  styleUrls: ['./investisseur-step.component.scss'],
})
export class InvestisseurStepComponent extends AbstractStepComponent {
  toggleCoInvControl = new FormControl<boolean>(false);
  conjoint: Investisseur | undefined;
  @ViewChild('investisseurField') investisseurField: SearchInvestisseurAutocompleteComponent;
  @ViewChild('coInvestisseurField') coInvestisseurField: SearchInvestisseurAutocompleteComponent;
  showCoInv: boolean;
  isOneInvestisseurOnly: boolean;
  private ngUnsuscribe = new Subject<void>();
  constructor(
    ficheConseilService: FicheConseilService,
    private dialogService: OuiDialogService,
    private formConfigService: FormConfigService
  ) {
    super(ficheConseilService);
  }
  public override isStepValid(): boolean {
    const Investisseurvalid = this.toggleCoInvControl.value
      ? !!this.currentFicheConseil?.investisseur && !!this.currentFicheConseil?.coInvestisseur
      : !!this.currentFicheConseil?.investisseur;
    return !!Investisseurvalid;
  }
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsuscribe.next();
    this.ngUnsuscribe.complete();
  }
  public markAsTouched() {
    this.investisseurField.markAsTouched();
    this.coInvestisseurField.markAsTouched();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.formConfigService.currentFormConfigSubject.pipe(takeUntil(this.ngUnsuscribe)).subscribe(config => {
      if (config) {
        // by default, we consider that we can have a co-investisseur
        // we check if there os a specific rule for only one investisseur
        const toggleCoInvestisseurRule = config.fieldConfigs.find(f => f.fieldKey === 'toggleCoInvestisseur');
        if (toggleCoInvestisseurRule && !toggleCoInvestisseurRule.isVisible) {
          this.isOneInvestisseurOnly = true;
        } else {
          this.isOneInvestisseurOnly = false;
        }
      }
    });
  }
  ngAfterViewInit() {
    this.toggleCoInvControl.valueChanges.subscribe(hasCoInv => {
      if (!hasCoInv) {
        this.ficheConseilService.onFicheConseilDataEdit('coInvestisseur', null);
      }
    });
  }

  public override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
    if (newFicheConseil) {
      if (newFicheConseil?.coInvestisseur) {
        this.toggleCoInvControl.setValue(true);
      }
    }
  }

  onInvestisseurPersonneSelected(inv: Investisseur | undefined) {
    this.conjoint = undefined;
    if (inv) {
      this.showCoInv = true;
      const newInvestisseur = {
        id: inv.id,
        nom: inv.investisseurEntite?.personnePhysique?.nom,
        prenom: inv.investisseurEntite?.personnePhysique?.prenom,
        displayName: inv.investisseurEntite?.displayName,
        code: inv.code,
        dateNaissance: inv.investisseurEntite?.personnePhysique?.dateNaissance,
        civiliteCode:
          inv.investisseurEntite?.personnePhysique?.genre === PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE ? 'MME' : 'MR',
        statutCodeNavigation: {
          libelle: inv.statutCodeNavigation?.libelle,
          code: inv.statutCodeNavigation?.code,
        },
        creationDate: inv.creationDate,
      };
      if (inv.personnePhysique && inv.personnePhysique.length > 0) {
        // cojoint are declared sometimes in relationCouplePersonnePhysique1 (conjoint is then personnePhysique2)
        // sometimes in  relationCouplePersonnePhysique2 (conjoint is then personnePhysique1)
        // most of the times in both (but not always)

        // we start to look in relationCouplePersonnePhysique1
        const relation1s = inv.personnePhysique[0].relationCouplePersonnePhysique1;
        if (relation1s && relation1s.length > 0) {
          const conjoints = relation1s[0].personnePhysique2.investisseur;
          if (conjoints && conjoints.length) {
            this.conjoint = conjoints[0];
          }
        }
        // then, if not found, we look in relationCouplePersonnePhysique2
        if (!this.conjoint) {
          const relation2s = inv.personnePhysique[0].relationCouplePersonnePhysique2;
          if (relation2s && relation2s.length > 0) {
            const conjoints = relation2s[0].personnePhysique1.investisseur;
            if (conjoints && conjoints.length) {
              this.conjoint = conjoints[0];
            }
          }
        }
      }

      this.ficheConseilService.onFicheConseilDataEdit('investisseur', newInvestisseur);
      return;
    }
    this.showCoInv = false;
    this.ficheConseilService.onFicheConseilDataEdit('investisseur', null);
    this.toggleCoInvControl.setValue(false);
  }

  onCoInvestisseurPersonneSelected(inv: Investisseur | undefined) {
    if (inv) {
      const newCoInvestisseur = {
        id: inv.id,
        nom: inv.investisseurEntite?.personnePhysique?.nom,
        prenom: inv.investisseurEntite?.personnePhysique?.prenom,
        displayName: inv.investisseurEntite?.displayName,
        code: inv.code,
        dateNaissance: inv.investisseurEntite?.personnePhysique?.dateNaissance,
        civiliteCode:
          inv.investisseurEntite?.personnePhysique?.genre === PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE ? 'MME' : 'MR',
        statutCodeNavigation: {
          libelle: inv.statutCodeNavigation?.libelle,
          code: inv.statutCodeNavigation?.code,
        },
        creationDate: inv.creationDate,
      };
      this.ficheConseilService.onFicheConseilDataEdit('coInvestisseur', newCoInvestisseur);
      return;
    }
    this.ficheConseilService.onFicheConseilDataEdit('coInvestisseur', null);
  }
}
